import React from 'react';
import { EmptyState, SkeletonList } from './Feedback';

/**
 * Responsive table.
 *
 * A data table cannot survive a 375px viewport, and the usual fallback —
 * horizontal scroll — makes rows unreadable. So this renders a real <table> at
 * `md` and above, and a stacked card list below it. Same data, same column
 * definitions, one source of truth.
 *
 * Columns: { key, header, render?, className?, align?, hideOnMobile?, primary? }
 * `primary` marks the field used as the card title on mobile.
 */

const alignClass = (align) =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

const cellValue = (col, row, index) => (col.render ? col.render(row, index) : row[col.key]);

const DataTable = ({
  columns,
  rows,
  keyField = '_id',
  loading = false,
  onRowClick,
  emptyTitle = 'Nothing here yet',
  emptyDescription,
  emptyIcon,
  emptyAction,
  caption,
  className = '',
}) => {
  if (loading) return <SkeletonList count={4} />;

  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        icon={emptyIcon}
        title={emptyTitle}
        description={emptyDescription}
        action={emptyAction}
      />
    );
  }

  const primary = columns.find((c) => c.primary) || columns[0];
  const secondary = columns.filter((c) => c !== primary && !c.hideOnMobile);

  return (
    <div className={className}>
      {/* Desktop: real table semantics. */}
      <div className="hidden overflow-hidden rounded-lg border border-border bg-surface shadow-sm md:block">
        <table className="w-full border-collapse text-sm">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b border-border bg-muted">
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-fg ${alignClass(
                    col.align
                  )} ${col.className || ''}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row[keyField] ?? i}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={[
                  'border-b border-border last:border-0 transition-colors duration-fast',
                  onRowClick ? 'cursor-pointer hover:bg-accent-soft' : 'hover:bg-muted/60',
                ].join(' ')}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 text-foreground ${alignClass(col.align)} ${col.className || ''}`}
                  >
                    {cellValue(col, row, i)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: the same rows as stacked cards, label above value. */}
      <div className="space-y-3 md:hidden">
        {rows.map((row, i) => (
          <div
            key={row[keyField] ?? i}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            role={onRowClick ? 'button' : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={
              onRowClick
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }
                : undefined
            }
            className={[
              'rounded-lg border border-border bg-surface p-4 shadow-sm',
              onRowClick ? 'cursor-pointer transition-shadow duration-base hover:shadow-md' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div className="mb-3 font-semibold text-foreground">{cellValue(primary, row, i)}</div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
              {secondary.map((col) => (
                <div key={col.key} className="min-w-0">
                  <dt className="text-xs uppercase tracking-wide text-muted-fg">{col.header}</dt>
                  <dd className="mt-0.5 truncate text-sm text-foreground">{cellValue(col, row, i)}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DataTable;
